// Achievement definitions and localStorage persistence.

import { CHAMPIONS } from "../data/champions";
import { RECRUIT_POOL } from "../scenes/rewards/ChampionRewards";

const HEALER_IDS = new Set(["Yuumi", "Soraka"]);

export interface AchievementDef {
  id: string;
  title: string;
  description: string;
  icon: string;
  target?: number; // if set, shows progress bar; unlocks when progress reaches target
}

const RUN_ACHIEVEMENTS: AchievementDef[] = [
  { id: "demacian_glory",      title: "Demacian Pride",    icon: "⚜️",  description: "Win a full run using only Demacian champions." },
  { id: "piltover_zaun_pride", title: "Progress and Might", icon: "⚙️", description: "Win a full run using only Piltover/Zaun champions." },
  { id: "kaisa_carry",         title: "Void Walker",        icon: "🌌", description: "Win a full run with Kai'Sa on your team." },
  { id: "one_star_only",       title: "Humble Beginnings",  icon: "⭐", description: "Beat the game using only 1-star units." },
  { id: "common_ground",       title: "Common People",      icon: "🪨", description: "Beat the game using only Common-tier units." },
  { id: "deathless",           title: "Untouchable",        icon: "🛡️", description: "Win a run without a single champion dying." },
  { id: "pure_skill",          title: "No Shortcuts",       icon: "🎯", description: "Win a run without any buff (red or blue) on your units." },
  { id: "sole_dealer",         title: "One-Man Army",       icon: "💀", description: "Win a run with only one champion ever dealing damage." },
  { id: "range_free",          title: "Up Close",           icon: "⚔️", description: "Win a run using only melee champions." },
  { id: "melee_free",          title: "Stand Back",         icon: "🏹", description: "Win a run using only ranged champions." },
  { id: "beat_hard",           title: "Hard Mode Victor",   icon: "💪", description: "Win a full run on Hard difficulty." },
  { id: "beat_impossible",     title: "The Impossible",     icon: "🔥", description: "Win a full run on Impossible difficulty." },
  { id: "impossible_no_legendary", title: "True Impossible", icon: "💎", description: "Win a run on Impossible difficulty without any legendary champions." },
];

const EASY_ACHIEVEMENTS: AchievementDef[] = [
  // Cross-run progress bar (stat-backed)
  { id: "first_victory",         title: "First Blood",      icon: "🩸", description: "Win your first combat.",                              target: 1       },
  { id: "ten_victories",         title: "Frequent Flyer",   icon: "✈️",  description: "Win 10 combats total.",                              target: 10      },
  { id: "twenty_five_victories", title: "Battle-Hardened",  icon: "⚔️",  description: "Win 25 combats.",                                    target: 25      },
  { id: "hundred_enemies",       title: "Death Dealer",     icon: "💀",  description: "Kill 100 enemies across all runs.",                  target: 100     },
  { id: "five_hundred_enemies",  title: "Warlord",          icon: "🏆",  description: "Kill 500 enemies across all runs.",                  target: 500     },
  { id: "total_healing",         title: "On the Mend",      icon: "💚",  description: "Heal 100,000 total HP across all runs.",             target: 100_000 },
  // Single-run milestones
  { id: "act_one_clear",         title: "Act I Victor",     icon: "🥇",  description: "Defeat the first act boss."                         },
  { id: "two_star",              title: "Rising Star",      icon: "⭐",  description: "Promote any champion to 2 stars during a run."      },
  { id: "three_star",            title: "Star Power",       icon: "🌟",  description: "Promote any champion to 3 stars."                   },
  { id: "full_roster",           title: "Full Roster",      icon: "👥",  description: "Fill your team with 3 champions."                   },
  // Single-combat achievements
  { id: "ability_barrage",       title: "Ability Barrage",  icon: "🔮",  description: "Cast 15 or more abilities in a single combat."      },
  { id: "cc_master",             title: "Crowd Control",    icon: "🌀",  description: "Apply 5 or more CC effects in a single combat."     },
];

const GRIND_ACHIEVEMENTS: AchievementDef[] = [
  { id: "fifty_victories",      title: "Seasoned",       icon: "🗡️",  description: "Win 50 combats.",                              target: 50        },
  { id: "hundred_victories",    title: "Centurion",      icon: "🎖️",  description: "Win 100 combats.",                             target: 100       },
  { id: "two_fifty_victories",  title: "Legend",         icon: "👑",   description: "Win 250 combats.",                             target: 250       },
  { id: "two_thousand_enemies", title: "Slaughterer",    icon: "☠️",   description: "Kill 2,000 enemies across all runs.",          target: 2_000     },
  { id: "ten_thousand_enemies", title: "Exterminator",   icon: "💣",   description: "Kill 10,000 enemies across all runs.",         target: 10_000    },
  { id: "three_runs",           title: "Triple Crown",   icon: "🏅",   description: "Complete 3 full runs.",                        target: 3         },
  { id: "ten_runs",             title: "True Veteran",   icon: "🦅",   description: "Complete 10 full runs.",                       target: 10        },
  { id: "healing_million",      title: "Lifeline",       icon: "🧬",   description: "Heal 1,000,000 total HP across all runs.",     target: 1_000_000 },
  { id: "thousand_casts",       title: "Spellslinger",   icon: "🌠",   description: "Cast 1,000 abilities across all runs.",        target: 1_000     },
  { id: "five_k_casts",         title: "Archmage",       icon: "🔯",   description: "Cast 5,000 abilities across all runs.",        target: 5_000     },
];

const DAMAGE_ACHIEVEMENTS: AchievementDef[] = Object.values(CHAMPIONS)
  .filter((def) => RECRUIT_POOL.has(def.id) && !HEALER_IDS.has(def.id))
  .map((def) => ({
    id: `damage_${def.id}`,
    title: `Million: ${def.displayName}`,
    icon: "💥",
    description: `Deal 1,000,000 total damage with ${def.displayName} across all runs.`,
    target: 1_000_000,
  }));

export const ACHIEVEMENTS: AchievementDef[] = [...RUN_ACHIEVEMENTS, ...EASY_ACHIEVEMENTS, ...GRIND_ACHIEVEMENTS, ...DAMAGE_ACHIEVEMENTS];

const ACHIEVEMENT_MAP = new Map(ACHIEVEMENTS.map((a) => [a.id, a]));

interface PersistentStats {
  totalCombatsWon: number;
  completedRuns: number;
  totalEnemiesKilled: number;
  totalHealingDone: number;
  totalAbilityCasts: number;
  /** Set true once the player has finished any run on Normal+ (gates Hard). */
  hardUnlocked?: boolean;
  /** Set true once the player has finished a run on Hard or Impossible (gates Impossible). */
  impossibleUnlocked?: boolean;
}

const LS_UNLOCKED        = "lolrogue_achievements_unlocked";
const LS_STATS           = "lolrogue_achievement_stats";
const LS_CHAMPION_DAMAGE = "lolrogue_champion_damage";
const LS_PROGRESS        = "lolrogue_achievement_progress";

export class AchievementStore {
  private unlocked: Set<string>;
  stats: PersistentStats;
  private championDamage: Record<string, number>;
  private progress: Record<string, number>;

  constructor() {
    try {
      const raw = localStorage.getItem(LS_UNLOCKED);
      this.unlocked = new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      this.unlocked = new Set();
    }
    try {
      const raw = localStorage.getItem(LS_STATS);
      this.stats = raw
        ? (JSON.parse(raw) as PersistentStats)
        : { totalCombatsWon: 0, completedRuns: 0, totalEnemiesKilled: 0, totalHealingDone: 0, totalAbilityCasts: 0 };
    } catch {
      this.stats = { totalCombatsWon: 0, completedRuns: 0, totalEnemiesKilled: 0, totalHealingDone: 0, totalAbilityCasts: 0 };
    }
    this.stats.totalEnemiesKilled ??= 0;
    this.stats.totalHealingDone ??= 0;
    this.stats.totalAbilityCasts ??= 0;
    // Migration: existing players (anyone with a completed run before the
    // difficulty rework) get all modes unlocked so the rename to Hard +
    // new Impossible doesn't strip access from people who already earned it.
    if (this.stats.hardUnlocked === undefined && this.stats.completedRuns > 0) {
      this.stats.hardUnlocked = true;
      this.stats.impossibleUnlocked = true;
      this.saveStats();
    }
    try {
      const raw = localStorage.getItem(LS_CHAMPION_DAMAGE);
      this.championDamage = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    } catch {
      this.championDamage = {};
    }
    try {
      const raw = localStorage.getItem(LS_PROGRESS);
      this.progress = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    } catch {
      this.progress = {};
    }
  }

  isUnlocked(id: string): boolean {
    return this.unlocked.has(id);
  }

  /** Returns the def if newly unlocked, null if already unlocked. */
  unlock(id: string): AchievementDef | null {
    if (this.unlocked.has(id)) return null;
    this.unlocked.add(id);
    this.save();
    return ACHIEVEMENT_MAP.get(id) ?? null;
  }

  getProgress(id: string): number {
    return this.progress[id] ?? 0;
  }

  /**
   * Sets absolute progress for an achievement.
   * Auto-unlocks when value >= target. Returns def if newly unlocked.
   */
  setProgress(id: string, value: number): AchievementDef | null {
    const def = ACHIEVEMENT_MAP.get(id);
    if (!def?.target || this.isUnlocked(id)) return null;
    this.progress[id] = value;
    try {
      localStorage.setItem(LS_PROGRESS, JSON.stringify(this.progress));
    } catch {}
    if (value >= def.target) return this.unlock(id);
    return null;
  }

  /** Unified progress value for UI — handles both progress store and champion damage. */
  getProgressValue(def: AchievementDef): number {
    if (def.id.startsWith("damage_")) {
      return this.getChampionDamage(def.id.replace("damage_", ""));
    }
    return this.progress[def.id] ?? 0;
  }

  /** Adds damage to a champion's cross-run total and returns the new total. */
  addChampionDamage(championId: string, amount: number): number {
    const next = (this.championDamage[championId] ?? 0) + amount;
    this.championDamage[championId] = next;
    try {
      localStorage.setItem(LS_CHAMPION_DAMAGE, JSON.stringify(this.championDamage));
    } catch {}
    return next;
  }

  getChampionDamage(championId: string): number {
    return this.championDamage[championId] ?? 0;
  }

  saveStats(): void {
    try {
      localStorage.setItem(LS_STATS, JSON.stringify(this.stats));
    } catch {}
  }

  private save(): void {
    try {
      localStorage.setItem(LS_UNLOCKED, JSON.stringify([...this.unlocked]));
    } catch {}
  }

  /** Reset everything — dev utility. */
  reset(): void {
    this.unlocked.clear();
    this.stats = { totalCombatsWon: 0, completedRuns: 0, totalEnemiesKilled: 0, totalHealingDone: 0, totalAbilityCasts: 0 };
    this.championDamage = {};
    this.progress = {};
    try {
      localStorage.removeItem(LS_UNLOCKED);
      localStorage.removeItem(LS_STATS);
      localStorage.removeItem(LS_CHAMPION_DAMAGE);
      localStorage.removeItem(LS_PROGRESS);
    } catch {}
  }
}
